// Admin authentication uses API-hosted HttpOnly cookies; token values never enter app state.

import type { User, ApiResponse } from '~/types';

interface LoginResponse {
  user: User;
  locker?: {
    lockerNumber: string;
    password: string;
  };
}

export const useAuth = () => {
  const config = useRuntimeConfig();
  const API = config.public.apiBaseUrl as string;

  const userCookie = useCookie<User | null>('a4t_admin_user', {
    maxAge: 60 * 60 * 24 * 7,
    sameSite: 'lax',
    path: '/',
    default: () => null,
  });

  // Computed properties
  const user = computed(() => userCookie.value);
  const sessionValidated = useState<boolean>('a4t-admin-session-validated', () => false);
  const isAuthenticated = computed(() => sessionValidated.value && !!userCookie.value);
  const isAdmin = computed(() => {
    const role = userCookie.value?.role;
    return role === 'admin' || role === 'super_admin' || role === 'warehouse_staff';
  });
  const isLoading = ref(false);

  // Login
  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string; locker?: LoginResponse['locker'] }> => {
    isLoading.value = true;
    try {
      const res = await $fetch<ApiResponse<LoginResponse>>(`${API}/auth/login`, {
        method: 'POST',
        body: { email, password },
        credentials: 'include',
        headers: { 'X-Auth-Transport': 'cookie' },
      });

      if (!res.success || !res.data) {
        return { success: false, error: res.error?.message || 'Login failed' };
      }

      const { user: u, locker } = res.data;

      // Check admin role
      if (!['admin', 'super_admin', 'warehouse_staff'].includes(u.role)) {
        await $fetch(`${API}/auth/logout`, {
          method: 'POST',
          body: {},
          credentials: 'include',
          headers: { 'X-Auth-Transport': 'cookie' },
        }).catch(() => undefined);
        return { success: false, error: 'You do not have admin access' };
      }

      userCookie.value = u;
      sessionValidated.value = true;

      return { success: true, locker };
    } catch (err: unknown) {
      const fetchErr = err as { data?: { error?: { message?: string } }; message?: string };
      const msg = fetchErr?.data?.error?.message || fetchErr?.message || 'Network error';
      return { success: false, error: msg };
    } finally {
      isLoading.value = false;
    }
  };

  // Refresh access token
  const refresh = async (): Promise<boolean> => {
    try {
      const res = await $fetch<ApiResponse<Record<string, never>>>(`${API}/auth/refresh`, {
        method: 'POST',
        body: {},
        credentials: 'include',
        headers: { 'X-Auth-Transport': 'cookie' },
      });
      return res.success;
    } catch {
      return false;
    }
  };

  // Fetch current user profile
  const fetchUser = async (): Promise<boolean> => {
    try {
      const res = await $fetch<ApiResponse<{ user: User }>>(`${API}/auth/me`, {
        credentials: 'include',
      });

      if (res.success && res.data?.user) {
        userCookie.value = res.data.user;
        sessionValidated.value = true;
        return true;
      }
      return false;
    } catch (err: unknown) {
      const fetchErr = err as { status?: number; statusCode?: number; response?: { status?: number } };
      const status = fetchErr.status || fetchErr.statusCode || fetchErr.response?.status;
      if (status !== 401 && status !== 403) {
        // A temporary outage or rate limit is not evidence that the session expired.
        // Stop navigation with an error rather than sending a signed-in admin to login.
        throw createError({ statusCode: status || 503, statusMessage: status === 429 ? 'Too many requests. Please try again shortly.' : 'Unable to verify your session. Please try again.' });
      }
      if (fetchErr?.status === 401 || fetchErr?.statusCode === 401) {
        // Try refresh
        const ok = await refresh();
        if (ok) {
          try {
            const retry = await $fetch<ApiResponse<{ user: User }>>(`${API}/auth/me`, {
              credentials: 'include',
            });
            if (retry.success && retry.data?.user) {
              userCookie.value = retry.data.user;
              sessionValidated.value = true;
              return true;
            }
          } catch {}
        }
        clearSession();
      }
      return false;
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    // Call logout API (best effort)
    await $fetch(`${API}/auth/logout`, {
      method: 'POST',
      body: {},
      credentials: 'include',
      headers: { 'X-Auth-Transport': 'cookie' },
    }).catch(() => undefined);

    clearSession();
    navigateTo('/login');
  };

  // Clear everything
  const clearSession = (): void => {
    userCookie.value = null;
    sessionValidated.value = false;
  };

  return {
    user,
    isAuthenticated,
    isAdmin,
    isLoading,
    login,
    logout,
    refresh,
    fetchUser,
    clearSession,
  };
};
